import {ToggleButton} from '@wandb/components';
import React, {type FC, memo, useCallback} from 'react';

import {trackSetting} from '../../services/analytics/workspaceSettingsEvents';
import {type RunHistoryKeyInfo, type RunHistoryKeyType} from '../../types/run';
import {type PanelProps} from '../../util/panelProps';
import {type RunsLinePlotConfig} from '../PanelRunsLinePlot/types';
import {useTrackSettingPage} from '../WorkspaceDrawer/Settings/hooks/useTrackSettingEvent';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import {MetricsPickerDropdown} from './MetricsPickerDropdown';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import {MetricsPickerRegexInput} from './MetricsPickerRegexInput';

export type MetricsPickerConfig = Pick<
  RunsLinePlotConfig,
  'metricRegex' | 'metrics' | 'useMetricRegex'
>;

export type MetricsPickerProps = {
  options: string[];
  keyTypes: {
    [key: string]: RunHistoryKeyType;
  };
  keyInfo: RunHistoryKeyInfo;
  config: MetricsPickerConfig;
  updateConfig: PanelProps<RunsLinePlotConfig>['updateConfig'];
};

export const MetricsPicker: FC<MetricsPickerProps> = memo(
  (props: MetricsPickerProps) => {
    if (props.config.useMetricRegex) {
      return <MetricsPickerRegexInput {...props} />;
    }

    return <MetricsPickerDropdown {...props} />;
  },
  metricsPickerPropsAreEqual
);

export function metricsPickerPropsAreEqual(
  prev: MetricsPickerProps,
  next: MetricsPickerProps
) {
  if (
    prev.config.useMetricRegex !== next.config.useMetricRegex ||
    prev.updateConfig !== next.updateConfig
  ) {
    return false;
  }

  if (next.config.useMetricRegex) {
    return (
      prev.config.metricRegex === next.config.metricRegex &&
      prev.keyInfo === next.keyInfo
    );
  }

  return (
    metricArraysEqual(prev.config.metrics, next.config.metrics) &&
    prev.options === next.options &&
    prev.keyTypes === next.keyTypes
  );
}

function metricArraysEqual(
  prevMetrics: string[] | undefined,
  nextMetrics: string[] | undefined
) {
  // MetricsPicker only consumes flat metric strings; keep the value fallback
  // here instead of stabilizing broad panel config upstream.
  if (prevMetrics === nextMetrics) {
    return true;
  }
  if (
    prevMetrics == null ||
    nextMetrics == null ||
    prevMetrics.length !== nextMetrics.length
  ) {
    return false;
  }
  for (let i = 0; i < prevMetrics.length; i++) {
    if (prevMetrics[i] !== nextMetrics[i]) {
      return false;
    }
  }
  return true;
}

export const MetricsRegexToggle = ({
  updateConfig,
  config,
}: MetricsPickerProps) => {
  const setMetricRegexMode = useSetMetricRegexMode({updateConfig, config});

  const onButtonClick = useCallback(() => {
    setMetricRegexMode(!config.useMetricRegex);
  }, [config.useMetricRegex, setMetricRegexMode]);
  const ariaLabel = config.useMetricRegex
    ? 'Disable regex matching'
    : 'Enable regex matching';

  return (
    <ToggleButton
      aria-label={ariaLabel}
      data-test="regex-button"
      icon="regex"
      isSelected={Boolean(config.useMetricRegex)}
      onPress={onButtonClick}
      variant="secondary"
      tooltip={{
        children: getRegexToggleTooltip(config.useMetricRegex),
        placement: 'top end',
      }}
    />
  );
};

function useSetMetricRegexMode({
  updateConfig,
  config,
}: Pick<MetricsPickerProps, 'updateConfig' | 'config'>) {
  const trackSettingPage = useTrackSettingPage();

  return useCallback(
    (useMetricRegex: boolean) => {
      if (config.useMetricRegex === useMetricRegex) {
        return;
      }

      updateConfig({
        useMetricRegex,
      });

      trackSetting({
        action: 'change',
        page: trackSettingPage,
        settingLevel: 'panel',
        setting: 'yAxis',
        settingValue: useMetricRegex.toString(),
        additionalProperties: {
          useMetricRegex,
        },
      });
    },
    [config.useMetricRegex, trackSettingPage, updateConfig]
  );
}

function getRegexToggleTooltip(useMetricRegex?: boolean) {
  if (useMetricRegex) {
    return 'Regex matching is ON — plot all metrics matching the query';
  }
  return 'Regex matching is OFF — plot selected metrics only';
}
